import { useCallback, useEffect, useState } from 'react';
import { Platform } from 'react-native';
import { useAuthStore, useModalStore } from '@/store';
import { subscribeAuthHydrated } from '@/lib/authHydration';
import {
  hasActionableSession,
  shouldPresentAuthenticated,
} from '@/lib/authPresentation';

/**
 * Gate any logged-out interaction behind the auth CTA modal.
 *
 * Returns a stable function `requireAuth(action, ctx?)`:
 *   - If the user is authenticated, calls `action()` immediately and returns
 *     whatever `action()` returns.
 *   - If the user is logged out, opens `AuthCTAModal` with the optional
 *     `ctx` props ({ title, subtitle, icon, source, ... }) and returns
 *     `undefined`. The caller's `action` is **not** queued for replay
 *     post-signup by default — pass an `onAuthSuccess` in `ctx` if you
 *     want the action to run after the user signs in.
 *
 * Convenience overload — pass an object to bind both at once:
 *   const requireAuth = useRequireAuth();
 *   <Pressable onPress={() => requireAuth(
 *     () => openModal('FileUploadModal'),
 *     { source: 'sidebar_upload', title: 'Sign in to upload files', icon: 'cloud-arrow-up' }
 *   )}>
 *
 * Helper hook `useIsAuthenticated()` is exported for places that just
 * want the boolean without opening modals.
 */
export default function useRequireAuth() {
  const token = useAuthStore(state => state.token);
  const isTokenExpired = useAuthStore(state => state.isTokenExpired);
  const openModal = useModalStore(state => state.actions.openModal);

  return useCallback((action, ctx = {}) => {
    const isAuthenticated = hasActionableSession({
      platform: Platform.OS,
      token,
      isTokenExpired: !!token && isTokenExpired(),
    });
    if (isAuthenticated) {
      return typeof action === 'function' ? action() : undefined;
    }
    openModal('AuthCTAModal', {
      source: ctx.source || 'unknown',
      title: ctx.title,
      subtitle: ctx.subtitle,
      icon: ctx.icon,
      primaryLabel: ctx.primaryLabel,
      secondaryLabel: ctx.secondaryLabel,
      showBenefits: ctx.showBenefits,
      onAuthSuccess: ctx.onAuthSuccess,
    });
    return undefined;
  }, [token, isTokenExpired, openModal]);
}

export function useIsAuthenticated() {
  const token = useAuthStore(state => state.token);
  const isTokenExpired = useAuthStore(state => state.isTokenExpired);
  return hasActionableSession({
    platform: Platform.OS,
    token,
    isTokenExpired: !!token && isTokenExpired(),
  });
}

/**
 * Auth state for rendering app chrome and account-owned surfaces.
 *
 * Unlike `useIsAuthenticated`, Android deliberately presents the signed-in
 * shell while AsyncStorage hydrates or a stored token refreshes. Do not use
 * this hook to authorize network requests or paid actions.
 */
export function useAuthPresentation() {
  const isAuthHydrated = useAuthHydrated();
  const token = useAuthStore(state => state.token);
  const isTokenExpired = useAuthStore(state => state.isTokenExpired);

  return shouldPresentAuthenticated({
    platform: Platform.OS,
    isAuthHydrated,
    token,
    isTokenExpired: !!token && isTokenExpired(),
  });
}

/**
 * True once the persisted auth store has finished rehydrating.
 *
 * On native (AsyncStorage) hydration is asynchronous, so on first render the
 * auth state is still the default (`token: null`). Screens must wait for this
 * before treating `!isAuthenticated` as "logged out" — otherwise a logged-in
 * user briefly renders the anonymous/empty UI (and personal fetches get
 * skipped) during the hydration window. On web (synchronous localStorage)
 * this is effectively always `true` on first render.
 *
 * Never stays `false` forever: zustand does not notify anyone when a storage
 * read or parse fails, and the native shells block their whole UI on this, so
 * `subscribeAuthHydrated` backs the subscription with a failsafe.
 */
export function useAuthHydrated() {
  const [hydrated, setHydrated] = useState(
    () => useAuthStore.persist?.hasHydrated?.() ?? true,
  );

  useEffect(() => {
    if (hydrated) return undefined;
    return subscribeAuthHydrated(useAuthStore.persist, () => setHydrated(true));
  }, [hydrated]);

  return hydrated;
}

/**
 * Hydration-aware "is there a logged-in user?" signal. Returns:
 *   - `hasUser`: token-based auth, but only `true` once hydration is done.
 *   - `isAuthResolved`: whether auth state is known yet (hydration complete).
 *
 * Gate personal data fetches on `hasUser` (include it in effect deps so the
 * fetch re-fires when hydration flips it true), and gate logged-out UI on
 * `isAuthResolved && !hasUser` so the anonymous view never flashes for a
 * logged-in user mid-hydration.
 */
export function useHasUser() {
  const isAuthResolved = useAuthHydrated();
  const isAuthenticated = useIsAuthenticated();
  return { hasUser: isAuthResolved && isAuthenticated, isAuthResolved };
}
